import { API_BASE_URL } from './api';
import { getAccessToken } from './auth';

const FAVORITES_URL = `${API_BASE_URL}/me/favorites`;

export async function loadUserFavoriteTemplateIds(): Promise<string[]> {
  const response = await authFetch(FAVORITES_URL, {
    method: 'GET',
    cache: 'no-store'
  });

  const payload = (await response.json()) as { templateIds?: unknown };
  if (!Array.isArray(payload.templateIds)) {
    return [];
  }

  return normalizeFavoriteTemplateIds(payload.templateIds);
}

export async function addUserFavoriteTemplateId(templateId: string): Promise<void> {
  const id = normalizeFavoriteTemplateId(templateId);
  if (!id) {
    throw new Error('Template id is missing.');
  }

  await authFetch(`${FAVORITES_URL}/${encodeURIComponent(id)}`, {
    method: 'POST'
  });
}

export async function removeUserFavoriteTemplateId(templateId: string): Promise<void> {
  const id = normalizeFavoriteTemplateId(templateId);
  if (!id) {
    throw new Error('Template id is missing.');
  }

  await authFetch(`${FAVORITES_URL}/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

async function authFetch(url: string, init: RequestInit): Promise<Response> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Sign in is required.');
  }

  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers || {})
    }
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Favorites request failed.'));
  }

  return response;
}

function normalizeFavoriteTemplateIds(ids: unknown[]): string[] {
  return Array.from(
    new Set(
      ids
        .map((id) => normalizeFavoriteTemplateId(id))
        .filter((id): id is string => Boolean(id))
    )
  );
}

function normalizeFavoriteTemplateId(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: string };
    return String(payload?.error || '').trim() || fallback;
  } catch {
    return fallback;
  }
}
